// Auth state: the bearer token (memory + localStorage so a refresh
// doesn't log you out), the current User, and login/logout actions.
//
// Phase 0 keeps this in a React context inside apps/web. Phase 1 extracts
// it to packages/auth (adds useRole / <RequireRole> / permission map) so
// the mobile app shares it. The shape here is deliberately the shape that
// extraction will keep.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { api, setAuthToken, setOnUnauthorized } from "@/lib/api";
import { ApiError, type User } from "@noc/api";

const TOKEN_KEY = "noc.token";

type AuthStatus = "loading" | "authed" | "anon";

interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  /** Resolves true on success. Throws ApiError on bad creds / rate-limit (caller shows the message). */
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<User | null>(null);
  const tokenRef = useRef<string | null>(null);

  const applyToken = useCallbackRef((token: string | null) => {
    tokenRef.current = token;
    setAuthToken(token);
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  });

  const clearSession = useCallbackRef(() => {
    applyToken(null);
    setUser(null);
    setStatus("anon");
  });

  // Bootstrap: if we have a stored token, validate it via /me.
  useEffect(() => {
    let cancelled = false;
    const stored = localStorage.getItem(TOKEN_KEY);
    if (!stored) {
      setStatus("anon");
      return;
    }
    applyToken(stored);
    api.auth
      .me()
      .then((res) => {
        if (cancelled) return;
        if (res.ok && res.user) {
          setUser(res.user);
          setStatus("authed");
        } else {
          clearSession();
        }
      })
      .catch(() => {
        if (!cancelled) clearSession();
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wire the client's 401 handler to drop the session.
  useEffect(() => {
    setOnUnauthorized(() => clearSession());
    return () => setOnUnauthorized(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallbackRef(async (username: string, password: string): Promise<boolean> => {
    const res = await api.auth.login({ username, password });
    if (!res.ok || !res.token || !res.user) {
      throw new ApiError(401, res.error ?? "invalid username or password", res);
    }
    applyToken(res.token);
    setUser(res.user);
    setStatus("authed");
    return true;
  });

  const logout = useCallbackRef(async (): Promise<void> => {
    try {
      await api.auth.logout();
    } catch {
      // best-effort; the bearer token is stateless server-side
    }
    clearSession();
  });

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, login, logout }),
    [status, user, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

// useCallbackRef: a stable callback that always sees the latest closure
// without listing deps. Avoids the "stale closure" footgun in the effects
// above while keeping the returned fn referentially stable. (Tiny local
// helper; Phase 1 can swap for the version from a shared hooks package.)
function useCallbackRef<T extends (...args: never[]) => unknown>(fn: T): T {
  const ref = useRef(fn);
  useEffect(() => {
    ref.current = fn;
  });
  return useMemo(() => ((...args: never[]) => ref.current(...args)) as T, []);
}
